import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, X, ArrowRight, ArrowLeft, BookOpen, FolderOpen, Calendar, Mic, ImagePlus, Sparkles, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OnboardingModal({ user, onComplete }) {
  const [suggestedFolders, setSuggestedFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [customFolder, setCustomFolder] = useState('');
  const [customFolders, setCustomFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const customInputRef = useRef(null);
  const [step, setStep] = useState(0); // 0,1,2 = intro cards, 3 = folder selection

  const MIN_FOLDERS = 5;

  useEffect(() => {
    const fetchSuggestedFolders = async () => {
      try {
        const response = await axios.get(`${API}/onboarding/suggested-folders`);
        setSuggestedFolders(response.data.folders);
        setSelectedFolders([]);
      } catch (err) {
        console.error('Failed to fetch suggested folders:', err);
        setError('Failed to load folder suggestions');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSuggestedFolders();
  }, []);

  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const toggleFolder = (folderName) => {
    setSelectedFolders(prev =>
      prev.includes(folderName)
        ? prev.filter(f => f !== folderName)
        : [...prev, folderName]
    );
  };

  const addCustomFolder = () => {
    const trimmed = customFolder.trim();
    if (trimmed && !customFolders.includes(trimmed) && !selectedFolders.includes(trimmed)) {
      setCustomFolders(prev => [...prev, trimmed]);
      setSelectedFolders(prev => [...prev, trimmed]);
      setCustomFolder('');
      setShowCustomInput(false);
    }
  };

  const removeCustomFolder = (folderName) => {
    setCustomFolders(prev => prev.filter(f => f !== folderName));
    setSelectedFolders(prev => prev.filter(f => f !== folderName));
  };

  const handleComplete = async () => {
    if (selectedFolders.length < MIN_FOLDERS) {
      setError(`Please select at least ${MIN_FOLDERS} folders to get started`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/onboarding/complete`, {
        selected_folders: selectedFolders.filter(f => !customFolders.includes(f)),
        custom_folders: customFolders
      });
      onComplete();
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save your selections. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Intro cards data
  const introCards = [
    {
      title: 'Journal',
      subtitle: 'Write your story',
      description: 'Capture memories by writing, recording audio, or adding photos. Build a daily streak and watch your plant grow.',
      icon: BookOpen,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      preview: (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary" />
            </div>
            <span className="text-xs text-muted-foreground">Today</span>
          </div>
          <div className="border border-dashed border-border rounded-lg p-3">
            <p className="text-sm text-muted-foreground/50 italic">What's on your mind today?</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <ImagePlus className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <Mic className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] text-primary font-medium">🌱 Streak: 1</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Stories',
      subtitle: 'Organize your moments',
      description: 'Your journal entries are sorted into topic folders automatically. Create custom topics or events, and share with people you trust.',
      icon: FolderOpen,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      preview: (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {['Family', 'Travel', 'Work', 'Health'].map((name, i) => (
              <div key={name} className="bg-card border border-border rounded-xl p-3 space-y-2">
                <FolderOpen className="w-4 h-4 text-primary" />
                <p className="text-xs font-serif font-medium text-foreground">{name}</p>
                <span className="text-[10px] font-semibold text-primary">{[3, 7, 2, 5][i]}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Timeline',
      subtitle: 'Relive any day',
      description: 'Every memory is placed on your timeline. Scroll through months and tap any day to revisit what you wrote.',
      icon: Calendar,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      preview: (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-serif font-medium text-foreground">March 2026</span>
            <div className="flex space-x-1">
              <ArrowLeft className="w-3 h-3 text-muted-foreground" />
              <ArrowRight className="w-3 h-3 text-muted-foreground" />
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <span key={i} className="text-[8px] text-muted-foreground font-medium">{d}</span>
            ))}
            {Array.from({ length: 31 }, (_, i) => {
              const hasEntry = [2, 5, 8, 11, 14, 17, 20, 23, 26].includes(i);
              const isToday = i === 14;
              return (
                <div key={i} className={`w-full aspect-square rounded-full flex items-center justify-center text-[8px] ${
                  isToday ? 'bg-primary text-white font-bold' : hasEntry ? 'text-foreground font-medium' : 'text-muted-foreground/40'
                }`}>
                  {i + 1}
                  {hasEntry && !isToday && <span className="absolute w-1 h-1 rounded-full bg-primary mt-3" />}
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
  ];

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 200 : -200, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 200 : -200, opacity: 0 }),
  };

  // Intro cards (steps 0-2)
  if (step < 3) {
    const card = introCards[step];
    const Icon = card.icon;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <div className="max-w-sm w-full">
          <AnimatePresence mode="wait" custom={1}>
            <motion.div
              key={step}
              custom={1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Card header */}
              <div className="p-6 pb-4 text-center">
                <div className={`w-12 h-12 rounded-full ${card.bgColor} flex items-center justify-center mx-auto mb-3`}>
                  <Icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <h2 className="text-xl font-serif font-semibold text-foreground">{card.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{card.subtitle}</p>
              </div>

              {/* Preview mockup */}
              <div className="px-6 pb-4">
                {card.preview}
              </div>

              {/* Description */}
              <div className="px-6 pb-4">
                <p className="text-sm text-muted-foreground text-center leading-relaxed">{card.description}</p>
              </div>

              {/* Navigation */}
              <div className="px-6 pb-6">
                {/* Progress dots */}
                <div className="flex items-center justify-center space-x-2 mb-4">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === step ? 'w-6 bg-primary' : 'w-1.5 bg-border'
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  {step > 0 ? (
                    <button
                      onClick={() => setStep(step - 1)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                  ) : (
                    <div />
                  )}
                  <button
                    onClick={() => setStep(step + 1)}
                    className="px-5 py-2.5 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors flex items-center space-x-2"
                  >
                    <span>{step === 2 ? 'Get Started' : 'Next'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Folder selection (step 3)
  const remainingToSelect = MIN_FOLDERS - selectedFolders.length;
  const canContinue = selectedFolders.length >= MIN_FOLDERS;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 pb-4 border-b border-border">
          <h2 className="text-xl font-serif font-semibold text-foreground mb-1">Welcome, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-sm text-muted-foreground">Select at least {MIN_FOLDERS} folders to organize your stories</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {suggestedFolders.map((folder) => {
              const isSelected = selectedFolders.includes(folder.name);
              return (
                <button
                  key={folder.name}
                  onClick={() => toggleFolder(folder.name)}
                  className={`p-3 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-primary text-white border-2 border-foreground shadow-md'
                      : 'bg-secondary/30 text-foreground border-2 border-transparent hover:bg-secondary/50'
                  }`}
                >
                  <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-foreground'}`}>
                    {folder.name}
                  </p>
                </button>
              );
            })}

            {customFolders.map((folder) => (
              <div
                key={folder}
                className="p-3 rounded-xl bg-primary text-white border-2 border-foreground shadow-md relative"
              >
                <p className="text-sm font-medium text-white pr-6">{folder}</p>
                <button
                  onClick={() => removeCustomFolder(folder)}
                  className="absolute top-2 right-2 text-white/70 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {showCustomInput ? (
              <div className="p-2 rounded-xl border-2 border-dashed border-primary bg-primary/5">
                <input
                  ref={customInputRef}
                  type="text"
                  value={customFolder}
                  onChange={(e) => setCustomFolder(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') addCustomFolder();
                  }}
                  onBlur={() => {
                    if (!customFolder.trim()) setShowCustomInput(false);
                  }}
                  placeholder="Type folder name..."
                  className="w-full bg-transparent text-sm font-medium focus:outline-none placeholder:text-muted-foreground"
                  maxLength={30}
                />
                <div className="flex justify-end mt-1 space-x-1">
                  <button
                    onClick={() => {
                      setCustomFolder('');
                      setShowCustomInput(false);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addCustomFolder}
                    disabled={!customFolder.trim()}
                    className="text-xs text-primary font-medium px-2 py-1 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomInput(true)}
                className="p-3 rounded-xl border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center space-x-2"
              >
                <PlusCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Add Custom</span>
              </button>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive mt-4">{error}</p>
          )}
        </div>

        <div className="p-6 pt-4 border-t border-border bg-secondary/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-foreground">
                {selectedFolders.length} / {MIN_FOLDERS}
              </p>
              <p className="text-xs text-muted-foreground">
                {canContinue ? 'Ready to continue!' : `Select ${remainingToSelect} more`}
              </p>
            </div>
            {canContinue && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={handleComplete}
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent"></div>
                    <span>Setting up...</span>
                  </>
                ) : (
                  <>
                    <span>Continue</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default OnboardingModal;
